import type { AppModuleRoute } from '../../../app/navigation'
import { useAuthSession } from '../../auth/session'
import { useTranslation } from 'react-i18next'
import { PlaceholderStateAsset, StateSurface } from '@/components/app/StateSurface'

export function ModulePlaceholderPage({
  route,
}: {
  route: AppModuleRoute
}) {
  const { t } = useTranslation()
  const { user } = useAuthSession()

  const roleNames = user?.roles
    .map((role) => role.name ?? role.slug ?? t('common.unknown'))
    .join(' / ')

  return (
    <section className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
      <div className="space-y-4">
        <StateSurface
          variant="section"
          size="md"
          tone="warning"
          asset={<PlaceholderStateAsset size="md" className="opacity-90" />}
          eyebrow={t(route.eyebrowKey)}
          title={t(route.titleKey)}
          description={t(route.descriptionKey)}
        />

        <div className="grid gap-4 md:grid-cols-2">
          <article className="app-panel-muted border border-border/70">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">
              {t('system.modulePlaceholder.currentOperator')}
            </p>
            <p className="mt-3 text-lg font-semibold text-foreground">
              {user?.display_name ?? t('system.modulePlaceholder.unknownUser')}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">{roleNames}</p>
          </article>

          <article className="app-panel-muted border border-border/70">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">
              {t('system.modulePlaceholder.activeBusinessScope')}
            </p>
            <p className="mt-3 text-lg font-semibold text-foreground">
              {user?.primary_business?.display_name ?? t('system.modulePlaceholder.globalPlatformScope')}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              {t('system.modulePlaceholder.scopeDescription')}
            </p>
          </article>
        </div>
      </div>

      <aside className="space-y-5">
        <article className="app-panel rounded-xl border border-border bg-card">
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">
            {t('system.modulePlaceholder.integrationReadyBoundary')}
          </p>
          <p className="mt-4 text-sm leading-7 text-muted-foreground">
            {t('system.modulePlaceholder.integrationDescription')}
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            {route.permissions.map((permissionId) => (
              <span
                key={permissionId}
                className="rounded-full border border-border/70 bg-muted/40 px-3 py-1 text-xs text-foreground"
              >
                {permissionId}
              </span>
            ))}
          </div>
        </article>

        <article className="app-panel-muted border border-dashed border-border/80">
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">
            {t('system.modulePlaceholder.nextImplementationFocus')}
          </p>
          <ul className="mt-4 space-y-3 text-sm leading-7 text-muted-foreground">
            <li>{t('system.modulePlaceholder.focus.connectEndpoints')}</li>
            <li>{t('system.modulePlaceholder.focus.states')}</li>
            <li>{t('system.modulePlaceholder.focus.permissions')}</li>
          </ul>
        </article>
      </aside>
    </section>
  )
}
